import { ActionType, AppConstants, durationInDays, formatDate, toDateOnly } from '@aston/foundation';
import { ISubsidiaryAppliedUserDelegations, ISubsidiaryFormUserDelegations, ISubsidiaryFormUserInactivity } from '@aston/middle-users-admin';

import { ISubsidiaryListItem, ISubsidiaryBlockedForm, ISubsidiaryForm, ISubsidiaryFromLegalIdentifier, ISubsidiaryDetail, TimeoutStatus, } from '../models';
import { ISubsidiaryListItemApi, ISubsidiaryFromLegalIdentifierApi, ISubsidiaryDetailApi, ISubsidiaryAppliedUserDelegationsApi, ISubsidiaryFormUserDelegationsApi, ISubsidiaryFormApi, ISubsidiaryBlockedFormApi, ISubsidiaryFormUserInactivityApi } from '../models/api';


export function mapToSubsidiaryListItemModel(apiModel: ISubsidiaryListItemApi): ISubsidiaryListItem {
	const accessEndDate = apiModel.accessEndDate ? new Date(apiModel.accessEndDate) : null;
	const daysToTimeout = accessEndDate ? Math.abs(durationInDays(new Date, accessEndDate)) : Number.POSITIVE_INFINITY;

	let timeoutStatus: TimeoutStatus = 'none';
	if (daysToTimeout < 31) {
		timeoutStatus = 'warning';
	}
	if (daysToTimeout < 7) {
		timeoutStatus = 'danger';
	}
	return {
		subsidiaryId: apiModel.subsidiaryId,
		tenantId: apiModel.tenantId,
		tenantName: apiModel.tenantName,
		currencyCode: apiModel.currencyCode,
		logoUrl: apiModel.logoUrl,
		accessLevel: apiModel.accessLevel,
		status: apiModel.status,
		isCgvAccepted: apiModel.isCgvAccepted,
		accessEndDate,
		daysToTimeout,
		timeoutStatus,
	};
}

export function mapToSubsidiaryUpdateFormApi(model: ISubsidiaryForm): ISubsidiaryFormApi {
	return {
		name: model.name,
		city: model.city,
		street: model.street,
		zipcode: model.zipCode,
		countryCode: model.countryCode,
		annualFigure: model.annualFigure,
		currencyCode: model.currencyCode,
		logo: model.logo.underlyingFile,
		isLogoDeleted: model.isLogoDeleted,
		isCgvAccepted: !!model.salesConditionsAccepted,
		cgvDocumentId: model.cgvDocumentId,
	};
}

export function mapToSubsidiaryCreateFormApi(model: ISubsidiaryForm): ISubsidiaryFormApi {
	return {
		legalIdentifier: model.legalIdentifier,
		name: model.name,
		city: model.city,
		street: model.street,
		zipcode: model.zipCode,
		countryCode: model.countryCode,
		annualFigure: model.annualFigure,
		currencyCode: model.currencyCode,
		logo: model.logo.underlyingFile,
		annualAccountingDocumentCount: +model.annualAccountingDocumentCount,
		isCgvAccepted: !!model.salesConditionsAccepted,
		cgvDocumentId: model.cgvDocumentId,
		timeZone: model.timeZone,
		databaseId: model.databaseId,
		forceNewCatalog: model.forceNewCatalog,
		brandingCode: model.brandingCode,
	};
}

export function mapToSubsidiaryBlockedFormApi(model: ISubsidiaryBlockedForm): ISubsidiaryBlockedFormApi {
	return {
		legalIdentifier: model.legalIdentifier,
		name: model.name,
		city: model.city,
		street: model.street,
		zipcode: model.zipCode,
		countryCode: model.countryCode,
		annualFigure: model.annualFigure,
		annualAccountingDocumentCount: +model.annualAccountingDocumentCount,
		languageCode: model.languageCode,
		contactMe: model.contactMe,
	};
}

export function mapToSubsidiaryFromLegalIdentifierModel(apiModel: ISubsidiaryFromLegalIdentifierApi): ISubsidiaryFromLegalIdentifier {
	return {
		legalIdentifierValidationStatus: apiModel.legalIdentifierValidationStatus,
		name: apiModel.name,
		zipCode: apiModel.physicalAddress?.zipCode,
		city: apiModel.physicalAddress?.city,
		street: apiModel.physicalAddress?.street,
		countryCode: apiModel.physicalAddress?.countryCode?.toUpperCase()
	};
}

export function mapToSubsidiaryDetailModel(apiModel: ISubsidiaryDetailApi): ISubsidiaryDetail {
	return {
		legalIdentifier: apiModel.legalIdentifier,
		name: apiModel.name,
		logoUrl: apiModel.logoUrl,
		annualFigure: apiModel.annualFigure,
		zipCode: apiModel.physicalAddress?.zipCode,
		city: apiModel.physicalAddress?.city,
		street: apiModel.physicalAddress?.street,
		countryCode: apiModel.physicalAddress?.countryCode?.toUpperCase(),
		currencyCode: apiModel.currencyCode,
		salesConditionsAccepted: !!apiModel.cgvDocumentId,
		cgvDocumentId: apiModel.cgvDocumentId,
	};
}

export function mapToUserDelegationsModel(apiModel: ISubsidiaryAppliedUserDelegationsApi): ISubsidiaryAppliedUserDelegations {
	return {
		...apiModel,
		userRoleDelegations: apiModel.userRoleDelegations || [],
		superDebtorDelegations: apiModel.superDebtorDelegations || [],
	};
}

export function mapToUserDelegationsFormModel(apiModel: ISubsidiaryFormUserDelegationsApi, mode: ActionType, forUserId: number): ISubsidiaryFormUserDelegations {
	return {
		userRoleDelegationIds: apiModel.userRoleDelegationIds || [],
		superDebtorDelegationIds: apiModel.superDebtorDelegationIds || [],
		begin: apiModel.begin ? new Date(apiModel.begin) : null,
		end: apiModel.end ? new Date(apiModel.end) : null,
		reactivateUser: false,
		forUserId,
		mode,
	};
}

export function mapToUserDelegationsApiModel(model: ISubsidiaryFormUserDelegations): ISubsidiaryFormUserDelegationsApi {
	return {
		userRoleDelegationIds: model.userRoleDelegationIds || [],
		superDebtorDelegationIds: model.superDebtorDelegationIds || [],
		begin: formatDate(model.begin || new Date, AppConstants.DATE_ONLY_FORMAT),
		end: formatDate(model.end || new Date, AppConstants.DATE_ONLY_FORMAT),
		reactivateUser: model.reactivateUser,
	};
}

export function mapToUserInactivityApiModel(model: ISubsidiaryFormUserInactivity): ISubsidiaryFormUserInactivityApi {
	return model.reactivateUser ? { begin: null, end: null } :
		{
			begin: model.begin ? toDateOnly(model.begin) : null,
		 	end: model.end ? toDateOnly(model.end) : null,
		};
}
